import { AnyAction } from '@reduxjs/toolkit'
import { AllowedRefs, decodeRefString } from 'lib/websocket-notify'
import { ThunkDispatch } from 'redux-thunk'
import { InvokePagedListRPC } from 'redux_toolkit/lib/createPagedListApi'
import { GRPCSubscriptionPagedList, ListFlags } from 'redux_toolkit/lib/GRPCSubscriptionPagedList'
import type { InvokeObjectRPC } from './createUnaryApi'
import { Flags, GRPCSubcription } from './GRPCSubscriptionObject'
import { markStale } from './multigetActions'
import { MultiGetRootState } from './multigetSlice'
import { ReduxListCache, ReduxListState, ReduxObjectCache, ReduxObjectState } from './reducer'
import { OBJECT_SUBSCRIPTIONS, LIST_SUBSCRIPTIONS } from './subscriptions'
import { ListableRequest, ListableResponse, compositeKey } from './utils'

export const SUBSCRIBE_OBJECT = 'SUBSCRIBE_OBJECT'
export const UNSUBSCRIBE = 'UNSUBSCRIBE'
export const LOADED = 'LOADED'
export const RESET_LIST = 'RESET'
export const ERRORED = 'ERRORED'
export const LOADING = 'LOADING'

// TODO: heap or trie or something for invalidation tags

export const SUBSCRIBE_LIST = 'SUBSCRIBE_LIST'
export const ADD_TO_LIST = 'ADD_TO_LIST'
export const INVALIDATE_TAG = 'INVALIDATE_TAG'
export const INVALIDATE_TAGS = 'INVALIDATE_TAGS'
export const DELETE_FROM_LIST = 'DELETE_FROM_LIST'
export const MODIFY_LIST = 'MODIFY_LIST'
export const MARK_DIRTY = 'MARK_DIRTY'
export const RESET_LIST_LOADING = 'RESET_LIST_LOADING'

export type ACTIONS =
    | typeof SUBSCRIBE_OBJECT
    | typeof UNSUBSCRIBE
    | typeof LOADED
    | typeof ERRORED
    | typeof SUBSCRIBE_LIST
    | typeof ADD_TO_LIST
    | typeof DELETE_FROM_LIST
    | typeof MODIFY_LIST
    | typeof INVALIDATE_TAG
    | typeof LOADING
    | typeof RESET_LIST
    | typeof MARK_DIRTY
    | typeof RESET_LIST_LOADING
    | typeof INVALIDATE_TAGS

export type mutatorFunction = (cachedValue: unknown, newValue: unknown) => unknown

type GlobalStoreState = ReduxListState<object> | ReduxObjectState<object> | MultiGetRootState

export const action = {
    subscribe:
        <Request extends object, Response extends object>(
        reducer: string,
        cacheKey: string,
        invokeRPC: InvokeObjectRPC<Request, Response>,
        opts?: Flags<Response>,
    ) =>
        async (
            dispatch: ThunkDispatch<ReduxObjectState<object>, never, AnyAction>,
            getState: () => ReduxObjectState<object>,
        ): Promise<unknown> => {
            const key = compositeKey(reducer, cacheKey)

            let sub = OBJECT_SUBSCRIPTIONS[key]
            if (!sub) {
                sub = OBJECT_SUBSCRIPTIONS[key] = new GRPCSubcription(dispatch, getState, reducer, cacheKey, invokeRPC, opts)
            }
                // TODO: wire through flag changes!
            return sub.subscribe()
        },
    poll:
        (reducer: string, cacheKey: string, refs: Array<AllowedRefs>, interval: number, cancelExisting: boolean) =>
            (): void => {
                const key = compositeKey(reducer, cacheKey)
                // poll = (refs: Array<AllowedRefs>, interval = 5000, cancelExisting = true)
                return LIST_SUBSCRIPTIONS[key].poll(refs, interval, cancelExisting)
            },
    unsubscribe: (reducer: string, cacheKey: string) => async (): Promise<boolean> => {
        const key = compositeKey(reducer, cacheKey)
        const r = OBJECT_SUBSCRIPTIONS[key]?.unsubscribe()
        return LIST_SUBSCRIPTIONS[key]?.unsubscribe() || r
    },
    // TODO: not a great API!
    invalidate: (reducer: string, cacheKey: string, allForReducerAndMethod: boolean) => async (): Promise<boolean> => {
        const cKey = compositeKey(reducer, cacheKey)
        if (!allForReducerAndMethod) {
            return !!OBJECT_SUBSCRIPTIONS[cKey]?.invalidate()
        }

        let i = false
        for (const k of Object.keys(OBJECT_SUBSCRIPTIONS)) {
            if (!k.startsWith(cKey)) {
                continue
            }
            i = true
            OBJECT_SUBSCRIPTIONS[k]?.invalidate()
        }
        return i
    },
    invalidateList:
        (reducer: string, cacheKey: string, allForReducerAndMethod: boolean) => async (): Promise<boolean> => {
            const cKey = compositeKey(reducer, cacheKey)
            if (!allForReducerAndMethod) {
                return !!LIST_SUBSCRIPTIONS[cKey]?.invalidate()
            }

            let i = false
            for (const k of Object.keys(LIST_SUBSCRIPTIONS)) {
                if (!k.startsWith(cKey)) {
                    continue
                }
                i = true
                LIST_SUBSCRIPTIONS[k]?.invalidate()
            }
            return i
        },
    subscribeList:
        <Request extends ListableRequest, Response extends ListableResponse>(
        reducer: string,
        cacheKey: string,
        invokeRPC: InvokePagedListRPC<Request, Response>,
        opts?: ListFlags<Response>,
    ) =>
        async (
            dispatch: ThunkDispatch<ReduxListState<object>, never, AnyAction>,
            getState: () => ReduxListState<object>,
        ): Promise<unknown> => {
            const key = compositeKey(reducer, cacheKey)

            let sub = LIST_SUBSCRIPTIONS[key]
            if (!sub) {
                sub = LIST_SUBSCRIPTIONS[key] = new GRPCSubscriptionPagedList(
                    dispatch,
                    getState,
                    reducer,
                    cacheKey,
                    invokeRPC,
                    opts,
                )
            }
                // TODO: wire through flag changes!
            return sub.subscribe()
        },
    getPage: (reducer: string, cacheKey: string, pageNumber: number, force: boolean) => async (): Promise<boolean> => {
        const key = compositeKey(reducer, cacheKey)

        const sub = LIST_SUBSCRIPTIONS[key]
        if (!sub) {
            return false
        }

        await sub.getPage(pageNumber, force)

        return true
    },
    getData:
        (reducer: string, cacheKey: string, force = false) =>
            async (): Promise<boolean> => {
                const key = compositeKey(reducer, cacheKey)

                const sub = OBJECT_SUBSCRIPTIONS[key]
                if (!sub) {
                    return false
                }

                await sub.getData(force)

                return true
            },
    invalidateTag: (
        tag: string,
        refString: string,
        item: unknown,
        mutator?: mutatorFunction,
    ) => async (dispatch: ThunkDispatch<GlobalStoreState, never, AnyAction>): Promise<boolean> => {
        const maybeMutateCachedValue = (state: ReduxObjectCache<unknown> | ReduxListCache<unknown>): unknown => {
            if (!mutator) {
                return item
            }
            if (!state) {
                return mutator(null, item)
            }

            const isObject = !('metadata' in state)
            if (isObject) {
                return mutator(state.tag === tag ? state.data : null, item)
            }

            if (state.metadata?.tag !== tag) {
                return mutator(null, item)
            }

            const position = state.metadata?.tags?.[refString] as [number, number]
            if (!position) {
                return mutator(null, item)
            }
            return mutator(state.data?.[position[0]]?.[position[1]], item)
        }

        // Handle regular API caches
        for (const sub of Object.values(LIST_SUBSCRIPTIONS)) {
            if (sub.tag === tag) {
                sub.invalidateTag(refString, maybeMutateCachedValue(sub.state))
            }
        }
        for (const sub of Object.values(OBJECT_SUBSCRIPTIONS)) {
            if (sub.tag === tag) {
                sub.invalidateTag(refString, maybeMutateCachedValue(sub.state))
            }
        }

        // Handle multiget cache - mark item as stale to trigger refetch
        // Dispatch with tag; reducer finds all caches with keys starting with "{tag}:"
        try {
            const refData = decodeRefString(refString) as AllowedRefs
            dispatch(markStale({ type: tag, ref: refData }))
        } catch (e) {
            // If refString parsing fails, skip multiget invalidation
            console.error('Failed to invalidate multiget')
        }

        return true
    },
}
